import { useState, useEffect, useCallback, useRef } from 'react';
import {
  api,
  ApiClientError,
  type Artifact,
  type ArtifactListParams,
  type ArtifactDetail,
  type Comment,
  type ActivityEvent,
  type Member,
  type AttentionItem,
  type User,
  type Workspace,
} from '../api/client';

interface UseApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

function useApiCall<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
  enabled = true,
): UseApiState<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(enabled);
  const [error, setError] = useState<string | null>(null);
  const hasLoadedOnceRef = useRef(false);

  const fetch = useCallback((mode: 'initial' | 'background' = 'background') => {
    if (!enabled) {
      setLoading(false);
      setError(null);
      hasLoadedOnceRef.current = false;
      return;
    }
    const showLoading = mode === 'initial' || !hasLoadedOnceRef.current;
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    Promise.resolve()
      .then(() => fetcher())
      .then((result) => {
        setData(result);
        hasLoadedOnceRef.current = true;
        if (showLoading) {
          setLoading(false);
        }
      })
      .catch((err) => {
        if (!(err instanceof ApiClientError && err.status === 401)) {
          setError(err.message || 'An error occurred');
        }
        if (showLoading) {
          setLoading(false);
        }
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ...deps]);

  useEffect(() => {
    fetch('initial');
  }, [fetch]);

  const refetch = useCallback(() => {
    fetch('background');
  }, [fetch]);

  return { data, loading, error, refetch };
}

export function useMe() {
  return useApiCall<{ user: User; workspace: Workspace }>(
    () => api.getMe(),
    [],
  );
}

export function useArtifacts(
  sort: 'recent' | 'alphabetical' | 'starred' | 'starred_only' = 'recent',
) {
  const result = useApiCall(
    () => api.listArtifacts({ sort }),
    [sort],
  );
  return {
    ...result,
    artifacts: result.data?.artifacts ?? [],
    total: result.data?.total ?? 0,
  };
}

interface UseArtifactFeedResult {
  artifacts: Artifact[];
  total: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => void;
  refetch: () => void;
}

const DEFAULT_PAGE_SIZE = 25;

// Module-level cache so data survives across navigations
const feedCache: Record<string, { artifacts: Artifact[]; total: number; offset: number; hasMore: boolean }> = {};

export function useArtifactFeed(query: ArtifactListParams): UseArtifactFeedResult {
  const serializedQuery = JSON.stringify({
    ...query,
    limit: query.limit ?? DEFAULT_PAGE_SIZE,
  });
  const cached = feedCache[serializedQuery];

  const [artifacts, setArtifacts] = useState<Artifact[]>(cached?.artifacts ?? []);
  const [total, setTotal] = useState(cached?.total ?? 0);
  const [offset, setOffset] = useState(cached?.offset ?? 0);
  const [hasMore, setHasMore] = useState(cached?.hasMore ?? false);
  const [loading, setLoading] = useState(!cached);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(!!cached);

  const fetchPage = useCallback(async (nextOffset: number, append: boolean) => {
    const requestQuery = {
      ...query,
      limit: query.limit ?? DEFAULT_PAGE_SIZE,
      offset: nextOffset,
    };
    const response = await api.listArtifacts(requestQuery);
    setTotal(response.total);
    setHasMore(response.has_more);
    const newOffset = response.next_offset ?? nextOffset + response.artifacts.length;
    setOffset(newOffset);
    setArtifacts((current) => {
      if (!append) {
        // Cache the first page for instant back-navigation
        feedCache[serializedQuery] = {
          artifacts: response.artifacts,
          total: response.total,
          offset: newOffset,
          hasMore: response.has_more,
        };
        return response.artifacts;
      }
      const existingIds = new Set(current.map((artifact) => artifact.id));
      const merged = current.slice();
      for (const artifact of response.artifacts) {
        if (!existingIds.has(artifact.id)) {
          merged.push(artifact);
          continue;
        }
        const index = merged.findIndex((item) => item.id === artifact.id);
        if (index !== -1) merged[index] = artifact;
      }
      return merged;
    });
  }, [query, serializedQuery]);

  const refetch = useCallback((mode: 'initial' | 'background' = 'background') => {
    const showLoading = mode === 'initial' || !hasLoadedOnce;
    if (showLoading) {
      setLoading(true);
    }
    setError(null);
    fetchPage(0, false)
      .then(() => setHasLoadedOnce(true))
      .catch((err) => {
        if (err instanceof ApiClientError && err.status === 401) return;
        setError(err.message || 'An error occurred');
      })
      .finally(() => {
        if (showLoading) {
          setLoading(false);
        }
      });
  }, [fetchPage, hasLoadedOnce]);

  useEffect(() => {
    refetch(hasLoadedOnce ? 'background' : 'initial');
  }, [hasLoadedOnce, refetch, serializedQuery]);

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    setLoadingMore(true);
    setError(null);
    fetchPage(offset, true)
      .catch((err) => {
        if (err instanceof ApiClientError && err.status === 401) return;
        setError(err.message || 'An error occurred');
      })
      .finally(() => setLoadingMore(false));
  }, [fetchPage, hasMore, loading, loadingMore, offset]);

  return {
    artifacts,
    total,
    loading,
    loadingMore,
    error,
    hasMore,
    loadMore,
    refetch,
  };
}

export function useArtifact(id: string) {
  const result = useApiCall<{ artifact: ArtifactDetail; role: string; effective_access: 'viewer' | 'commenter' | 'editor' | null }>(
    () => api.getArtifact(id),
    [id],
  );
  return {
    ...result,
    artifact: result.data?.artifact ?? null,
    role: result.data?.role ?? null,
    effectiveAccess: result.data?.effective_access ?? null,
  };
}

export function useComments(artifactId: string, version?: number, resolved?: boolean) {
  const result = useApiCall<{ comments: Comment[] }>(
    () => api.listComments(artifactId, version, resolved),
    [artifactId, version, resolved],
  );
  return {
    ...result,
    comments: result.data?.comments ?? [],
  };
}

export function useActivity() {
  const result = useApiCall<{ events: ActivityEvent[] }>(
    () => api.listActivity(),
    [],
  );
  return {
    ...result,
    events: result.data?.events ?? [],
  };
}

export function useUnreadCount(enabled = true) {
  const [refreshKey, setRefreshKey] = useState(0);
  const result = useApiCall<{ count: number }>(
    () => api.getUnreadCount(),
    [refreshKey],
    enabled,
  );

  // Listen for global event to refresh count
  useEffect(() => {
    const handler = () => setRefreshKey((k) => k + 1);
    window.addEventListener('exhibit:inbox-changed', handler);
    return () => window.removeEventListener('exhibit:inbox-changed', handler);
  }, []);

  return {
    ...result,
    count: result.data?.count ?? 0,
  };
}

/** Call this after marking inbox items as read to update the sidebar badge. */
export function notifyInboxChanged() {
  window.dispatchEvent(new CustomEvent('exhibit:inbox-changed'));
}

export function useAttentionItems() {
  const result = useApiCall<{ items: AttentionItem[] }>(
    () => api.getAttentionItems(),
    [],
  );
  return {
    ...result,
    items: result.data?.items ?? [],
  };
}

export function useMembers() {
  const result = useApiCall<{ members: Member[] }>(
    () => api.listMembers(),
    [],
  );
  return {
    ...result,
    members: result.data?.members ?? [],
  };
}

export function useWorkspace() {
  const result = useApiCall<{ workspace: Workspace }>(
    () => api.getWorkspace(),
    [],
  );
  return {
    ...result,
    workspace: result.data?.workspace ?? null,
  };
}
